import { Component, ElementRef, inject, OnDestroy, TemplateRef, viewChild, ViewContainerRef, signal } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { TooltipComponent } from '../tooltip/tooltip.component';
import { CommonModule } from '@angular/common';
import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'lib-error-cell-renderer',
  standalone: true,
  imports: [CommonModule, TooltipComponent, OverlayModule, MatIconModule],
  templateUrl: './error-cell-renderer.component.html',
  styleUrls: ['./error-cell-renderer.component.scss']
})
export class ErrorCellRenderer implements ICellRendererAngularComp, OnDestroy {
  params = signal<any>(undefined);
  value = signal<any>(undefined);
  hasError = signal(false);
  iconType = signal<string | null>(null);
  errorMessage = signal<string | undefined>(undefined);

  tooltipTemplate = viewChild.required<TemplateRef<any>>('tooltipTemplate');
  errorIcon = viewChild.required<ElementRef>('errorIcon');

  private overlayRef: OverlayRef | null = null;
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);


  agInit(params: any): void {
    this.params.set(params);
    this.value.set(
      params.valueFormatted ??
      this.resolveValue(params.value, params.colDef.field)
    );

    const error = params.data?.validationErrors?.[params?.colDef?.field];
    this.hasError.set(!!error);
    if (this.hasError()) {
      this.errorMessage.set(error);
    }

    this.iconType.set(params.cellRendererParams?.iconType ||
      this.detectIconType(params.colDef.cellEditor));
  }

  private resolveValue(value: any, field?: string): string {
    if (!value) return '';

    // string case
    if (typeof value === 'string') return value;

    // object case 
    if (typeof value === 'object' && field) {
      return value[field] ?? '';
    }

    return '';
  }

  ngOnDestroy(): void {
    this.hide();
  }

  refresh(params: any): boolean {
    this.agInit(params);
    return true;
  }

  show(): void {
    if (this.overlayRef) {
      return;
    }

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.params().eGridCell)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 0
        }
      ]);

    this.overlayRef = this.overlay.create({ positionStrategy, panelClass: 'error-tooltip-overlay' });
    const portal = new TemplatePortal(this.tooltipTemplate(), this.viewContainerRef);
    this.overlayRef.attach(portal);
  }

  hide(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private detectIconType(cellEditor: any): string | null {
    if (!cellEditor) return null;

    const editorName = cellEditor.name || cellEditor.toString();

    if (editorName.includes('DatePicker') || editorName.includes('Date')) {
      return 'calendar';
    }
    if (editorName.includes('Autocomplete') || editorName.includes('Select')) {
      return 'dropdown';
    }

    return null;
  }

  clear(event: MouseEvent) {
    event.stopPropagation();

    const field = this.params().colDef.field;

    this.params().data[field] = '';
    this.value.set('');

    if (this.params().data.validationErrors) {
      this.params().data.validationErrors[field] = false;
    }

    this.params().data.isDirty = true;

    this.params().api.applyTransaction({
      update: [this.params().data]
    });
  }
}
